import {useCallback, useEffect, useRef, useState} from 'react';
import {datadogLogs} from '@datadog/browser-logs';

import {VideoCallWidget} from '@/components/general/MHHeader/VideoCall/VideoCallWidget';
import {useInitializeDatadogRum} from '@/services/dataDogRum';
import {MEETING_ID_KEY, VIDEO_CALL_USER_KEY} from '@/services/videoService';
import {vidyoProviderLogs} from '@/utils/general';

import 'vidyoclient-nativewebrtc-sdk/dist/VidyoClient.css';

declare global {
    interface Window {
        tf: any;
        banubaIsLoaded: boolean;
    }
}

const DELAY_BEFORE_CLOSE = 3200; // ms

let beforeUnloadHandled = false;

export const VideoCall = () => {
    const [isReady, setIsReady] = useState(false);
    const endCallRef = useRef<ReturnType<typeof setTimeout>>(null);
    const meetingId = sessionStorage.getItem(MEETING_ID_KEY);
    const userJson = sessionStorage.getItem(VIDEO_CALL_USER_KEY);
    const user = JSON.parse(userJson) as {
        firstName: string;
        lastName: string;
        id: number;
    };

    useInitializeDatadogRum();

    const maximizeDatadogLogsBeforeClosingVideoTab = () => {
        if (beforeUnloadHandled) {
            return;
        }
        beforeUnloadHandled = true;
        datadogLogs.logger.info('Shutting down VideoCall screen');
    };

    useEffect(() => {
        const fn = async () => {
            if (window?.['VIDYO_IS_BANUBA_ENABLED'] === 'true' && !window.banubaIsLoaded) {
                return new Promise<void>((resolve, reject) => {
                    vidyoProviderLogs({
                        message: 'Loading Banuba plugin',
                        eventType: 'stream-effect',
                        status: 'info',
                    });
                    import('../../assets/banuba/BanubaPlugin')
                        .then((banubaPlugin) => {
                            void banubaPlugin.initBanubaPlugin();
                            window['banubaIsLoaded'] = true;
                            resolve();
                            setTimeout(() => {
                                setIsReady(true);
                            }, 50);
                            vidyoProviderLogs({
                                message: 'Banuba plugin loaded successfully',
                                eventType: 'stream-effect',
                                status: 'info',
                            });
                        })
                        .catch((error) => {
                            vidyoProviderLogs({
                                message: 'Failed to load Banuba plugin',
                                error: error instanceof Error ? error.stack : String(error),
                                status: 'error',
                                eventType: 'stream-effect',
                            });
                            reject();
                        });
                });
            }

            setIsReady(true);
        };

        // eslint-disable-next-line @typescript-eslint/no-floating-promises
        fn();
    }, []);

    const handleDisconnect = useCallback(() => {
        if (!('vidyoConnector' in window)) {
            return;
        }

        // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access, @typescript-eslint/no-unsafe-call
        window?.vidyoConnector?.Disconnect?.();
        // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access, @typescript-eslint/no-unsafe-call
        window?.vidyoConnector?.SetCameraPrivacy?.({
            privacy: true,
        });
        // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access, @typescript-eslint/no-unsafe-call
        window?.vidyoConnector?.SetMicrophonePrivacy?.({
            privacy: true,
        });
    }, []);

    const isDebug = new URLSearchParams(window.location.search).get('debug') === 'true';

    const handleEndCall = useCallback(() => {
        sessionStorage.removeItem(MEETING_ID_KEY);
        sessionStorage.removeItem(VIDEO_CALL_USER_KEY);

        clearTimeout(endCallRef.current);

        handleDisconnect();

        if (!isDebug) {
            endCallRef.current = setTimeout(() => {
                window.close?.();
            }, DELAY_BEFORE_CLOSE);
        }
    }, [handleDisconnect, isDebug]);

    useEffect(() => {
        document.addEventListener('beforeunload', handleEndCall);
        window.addEventListener('beforeunload', maximizeDatadogLogsBeforeClosingVideoTab);

        return () => {
            document.removeEventListener('beforeunload', handleEndCall);
            window.removeEventListener('beforeunload', maximizeDatadogLogsBeforeClosingVideoTab);
        };
    }, [handleEndCall]);

    return (
        <>
            {isReady && (
                <VideoCallWidget
                    currentMeetingId={meetingId}
                    onClose={handleEndCall}
                    onDisconnect={handleDisconnect}
                    user={{
                        firstName: user.firstName,
                        lastName: user?.lastName,
                        id: user?.id,
                    }}
                />
            )}
        </>
    );
};
